/**
 * Chart settings: everything the chart settings popup edits (screenshots/72 Chart, 76 Data, 77 Labels,
 * 78 Colors, 79 Interact), saved per widget in the overview layout (`config.settings`).
 *
 * - DataBinding → a dataset query (`bindingToQuery`). The Worker answers with a `Shape` (chartTypes.ts).
 * - Labels / Colors / Interact → layered onto the ECharts option from `buildOption` by `applySettings`,
 *   so every chart type gets custom labels, colors, reference lines and click behavior the same way.
 * All pure; tests/chartSettings.test.ts covers them.
 */
import type { EChartsCoreOption } from "echarts/core";
import type { Palette } from "./palette";
import { fmt, type ChartConfig, type ChartType, type Unit } from "./chartTypes";
import { contrast, simulate, type Check } from "./paletteChecks";

// ============================================================================ Data connection (datasets.ts)

// Named (not `export *`): the .mts tests load this file as CommonJS, where star re-exports aren't visible to ESM imports.
export { DATASETS, datasetsFor, acceptsField, bindingProblems, bindingToQuery } from "./datasets";
export type { FieldType, Field, DatasetId, Group, DatasetDef, Slot, Agg, Filter, DataBinding } from "./datasets";
import { DATASETS, type DataBinding, type DatasetId } from "./datasets";

// ============================================================================ Labels

export type NumberFormat = "full" | "compact" | "plain" | "percent";
export interface RefLine { id: string; kind: "average" | "median" | "value"; value?: number; label?: string; on: boolean }

export interface LabelSettings {
  title?: string;                      // widget header; supports {tokens}
  subtitle?: string;
  dataLabels: { show: boolean; position: "inside" | "end" | "outside"; format: NumberFormat; decimals: 0 | 1 | 2; prefix?: string; suffix?: string };
  renames: Record<string, string>;     // row key → display label
  renamePattern?: string;              // "Ald. {last} ({ward})", applied when there's no explicit rename
  axis: { value?: string; category?: string };
  legend: "top" | "right" | "bottom" | "off";
  tooltip?: string;                    // "{name} · {value:$,0} · Ward {ward}"
  referenceLines: RefLine[];
}

/** One number, the way the Labels tab says to show it. */
export function formatNumber(v: number, d: Pick<LabelSettings["dataLabels"], "format" | "decimals" | "prefix" | "suffix">, unit: Unit = "count"): string {
  const money = unit === "usd";
  let s: string;
  switch (d.format) {
    case "percent": s = `${(v * 100).toFixed(d.decimals)}%`; break;
    case "compact": {
      const a = Math.abs(v);
      const [div, suf] = a >= 1e6 ? [1e6, "M"] : a >= 1e3 ? [1e3, "K"] : [1, ""];
      s = `${money ? "$" : ""}${(v / div).toFixed(div === 1 ? 0 : d.decimals)}${suf}`; break;
    }
    case "plain": s = v.toLocaleString("en-US", { maximumFractionDigits: d.decimals }); break;
    default: s = money ? `$${v.toLocaleString("en-US", { maximumFractionDigits: d.decimals })}` : fmt(v, unit);
  }
  return `${d.prefix ?? ""}${s}${d.suffix ?? ""}`;
}

/**
 * Fill a label template. Tokens: {name} {value} {ward} {client} {since} … plus value formats:
 * {value:$,0} money · {value:$K,1} compact money · {value:,0} plain · {value:%,0} percent.
 * Unknown tokens stay visible as-is so a typo is obvious in the preview.
 */
export function fillTemplate(tpl: string, vars: Record<string, string | number | undefined>): string {
  return tpl.replace(/\{([a-zA-Z.]+)(?::([^}]*))?\}/g, (whole, key: string, spec?: string) => {
    const v = vars[key];
    if (v === undefined) return whole;
    if (spec === undefined || typeof v !== "number") return String(v);
    const m = /^(\$K|\$|%)?,(\d)$/.exec(spec);
    if (!m) return String(v);
    const decimals = Number(m[2]) as 0 | 1 | 2;
    const format: NumberFormat = m[1] === "$K" ? "compact" : m[1] === "%" ? "percent" : m[1] === "$" ? "full" : "plain";
    return formatNumber(v, { format, decimals }, m[1]?.startsWith("$") ? "usd" : "count");
  });
}

/** Display label for a row: explicit rename → pattern → original. */
export function displayName(key: string, label: string, vars: Record<string, string | number | undefined>, l: Pick<LabelSettings, "renames" | "renamePattern">): string {
  if (l.renames[key]) return l.renames[key];
  if (!l.renamePattern) return label;
  const words = label.trim().split(/\s+/);
  const last = /^(jr\.?|sr\.?|ii|iii|iv)$/i.test(words.at(-1) ?? "") && words.length > 1 ? words.at(-2)! : words.at(-1)!;
  return fillTemplate(l.renamePattern, { ...vars, name: label, first: words[0], last });
}

// ============================================================================ Colors

/** A token id ("chart-2", "muted", "seq-3", "positive", "forecast-for", …) or a hex color. */
export type ColorRef = string;
export interface ColorSettings {
  mode: "single" | "highlight" | "category" | "rules";
  single: ColorRef;
  highlight: { key?: string; color: ColorRef; others: ColorRef; label: boolean };
  byCategory: Record<string, ColorRef>;
  rules: { op: ">=" | ">" | "<=" | "<" | "="; value: number; color: ColorRef }[];
  elseColor: ColorRef;
}

export function resolveColor(ref: ColorRef, p: Palette): string {
  if (/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(ref)) return ref;
  const map: Record<string, string> = {
    "chart-1": p.c1, "chart-2": p.c2, "chart-3": p.c3, muted: p.muted, "chart-muted": p.muted,
    "seq-1": p.seq[0], "seq-2": p.seq[1], "seq-3": p.seq[2], "seq-4": p.seq[3],
    positive: p.positive, negative: p.negative, "forecast-for": p.forecastFor, "forecast-against": p.forecastAgainst, "forecast-tossup": p.tossup,
  };
  return map[ref] ?? p.c1;
}

const cmp = { ">=": (a: number, b: number) => a >= b, ">": (a: number, b: number) => a > b, "<=": (a: number, b: number) => a <= b, "<": (a: number, b: number) => a < b, "=": (a: number, b: number) => a === b };

export function colorFor(key: string, value: number, c: ColorSettings, p: Palette): string {
  switch (c.mode) {
    case "single": return resolveColor(c.single, p);
    case "highlight": return resolveColor(key === c.highlight.key ? c.highlight.color : c.highlight.others, p);
    case "category": return resolveColor(c.byCategory[key] ?? c.single, p);
    case "rules": { const r = c.rules.find((x) => cmp[x.op](value, x.value)); return resolveColor(r ? r.color : c.elseColor, p); }
  }
}

/**
 * The Accessibility list in the Colors tab. Uses the same contrast + Machado simulation as the theme checks.
 * `labelsShown` = every mark also has a value label, which is what makes a low-contrast "muted" acceptable.
 */
export function colorChecks(c: ColorSettings, p: Palette, labelsShown: boolean, surface = "#FFFFFF"): Check[] {
  const out: Check[] = [];
  const pairs: [string, string, string][] =
    c.mode === "highlight" ? [[resolveColor(c.highlight.color, p), resolveColor(c.highlight.others, p), "Highlight vs. muted"]]
    : c.mode === "rules" ? c.rules.map((r) => [resolveColor(r.color, p), resolveColor(c.elseColor, p), `Rule ${r.op} ${r.value} vs. else`] as [string, string, string])
    : [];
  const marks = c.mode === "highlight" ? [resolveColor(c.highlight.color, p)] : c.mode === "single" ? [resolveColor(c.single, p)]
    : c.mode === "rules" ? c.rules.map((r) => resolveColor(r.color, p)) : Object.values(c.byCategory).map((r) => resolveColor(r, p));
  for (const m of marks) {
    const cr = contrast(m, surface);
    const who = c.mode === "highlight" ? "Highlight" : c.mode === "single" ? "Bars" : m;
    out.push({ id: `mark-${m}`, label: "Mark contrast", state: cr >= 3 ? "pass" : labelsShown ? "warn" : "fail", detail: `${who} on white: ${cr.toFixed(1)}:1 (marks need 3:1)` });
  }
  const quiet = c.mode === "highlight" ? resolveColor(c.highlight.others, p) : c.mode === "rules" ? resolveColor(c.elseColor, p) : null;
  if (quiet) {
    const cr = contrast(quiet, surface);
    if (cr < 3) out.push({ id: "muted", label: "Muted bars", state: labelsShown ? "warn" : "fail",
      detail: `${cr.toFixed(1)}:1 on white. ${labelsShown ? "OK here because every bar shows its value." : "Turn on value labels or pick a darker color."}` });
  }
  for (const [a, b, label] of pairs) {
    const worst = Math.min(...(["protan", "deutan", "tritan", "gray"] as const).map((k) => contrast(simulate(a, k), simulate(b, k))));
    out.push({ id: `apart-${label}`, label, state: worst >= 2 ? "pass" : worst >= 1.5 ? "warn" : "fail",
      detail: `${label}: at least ${worst.toFixed(1)}:1 apart in every color-vision simulation` });
  }
  if (c.mode === "highlight") out.push({ id: "cue", label: "Not color-only", state: c.highlight.label ? "pass" : "warn",
    detail: c.highlight.label ? "Highlighted bar is labelled, so color isn’t the only cue" : "Label the highlighted bar so color isn’t the only cue" });
  return out;
}

// ============================================================================ Interactions

export interface InteractionSettings {
  click: "filter" | "open" | "drill" | "none";
  drillTo?: string;                   // next grouping field, e.g. "date"
  tooltip: boolean;
  emphasis: "self" | "series" | "none";
  dimOthers: boolean;
  enter: "grow" | "fade" | "none";
  update: "morph" | "flash" | "none";
  duration: number;                   // 0–800 ms
  easing: "cubicOut" | "quarticOut" | "linear";
  /** Zoom & brush for time charts (ECharts dataZoom, screenshots/89). The range is saved in the widget's `config.zoom`. */
  zoom?: boolean;
}

// ============================================================================ Table type (screenshots/87)

export type ColumnFormat = "text" | "link" | "number" | "usd" | "usdBar" | "pct" | "date";
export interface TableColumn { field: string; label: string; format: ColumnFormat; visible: boolean }
export interface HighlightRule { field: string; op: ">=" | "<=" | "="; value: number | string; style: "boldTint" | "outline" }
export interface TableSettings {
  columns: TableColumn[];                 // in display order (drag to reorder)
  sortBy?: { field: string; dir: "asc" | "desc" };
  rows: 5 | 8 | 12 | "all";
  density: "compact" | "comfortable";
  totalRow: boolean;
  freezeFirst: boolean;
  highlights: HighlightRule[];            // value may be "{viewer.ward}"
}

export const defaultTable = (): TableSettings => ({
  columns: [{ field: "label", label: "Name", format: "link", visible: true }, { field: "value", label: "Value", format: "number", visible: true }],
  rows: 8, density: "comfortable", totalRow: false, freezeFirst: false, highlights: [],
});

/** The table a binding starts with: the grouping (as links), the measure, then the tooltip extras (87). */
export function tableFor(s: Pick<ChartSettings, "table" | "data">): TableSettings {
  if (s.table) return s.table;
  const ds = DATASETS[s.data.datasetId];
  const fieldOf = (id?: string) => ds.fields.find((f) => f.id === id);
  const v = fieldOf(s.data.mapping.value.field);
  const money = v?.type === "currency";
  return { ...defaultTable(), columns: [
    { field: "label", label: fieldOf(s.data.mapping.category)?.label ?? "Name", format: "link", visible: true },
    ...s.data.mapping.tooltip.map((id): TableColumn => ({ field: id, label: fieldOf(id)?.label ?? id, format: fieldOf(id)?.type === "currency" ? "usd" : fieldOf(id)?.type === "number" || fieldOf(id)?.type === "ward" ? "number" : "text", visible: true })),
    { field: "value", label: v?.label ?? "Count", format: money ? "usdBar" : "number", visible: true },
  ], sortBy: { field: "value", dir: "desc" } };
}

export interface TableRow { key: string; href?: string; values: Record<string, string | number | undefined> }
export interface BuiltTable {
  columns: { field: string; label: string; format: ColumnFormat; max?: number }[];
  rows: { key: string; href?: string; cells: { text: string; value?: number; bar?: number }[]; style?: HighlightRule["style"] }[];
  total?: { label: string; cells: (string | null)[] };
  more: number;
}

/**
 * Rows + settings → what the Table type draws. `allRowsTotal` lets the total row show the dataset's full total
 * (e.g. all 22 alderpersons), not just the rows that fit. Viewer tokens in highlight rules resolve client-side
 * from the signed-in viewer (they only affect styling, never which data is returned).
 */
export function buildTable(rows: TableRow[], t: TableSettings, opts: { unit?: Unit; viewer?: Record<string, string | number>; allRowsTotal?: number } = {}): BuiltTable {
  const cols = t.columns.filter((c) => c.visible);
  const sorted = t.sortBy ? [...rows].sort((a, b) => {
    const x = a.values[t.sortBy!.field], y = b.values[t.sortBy!.field];
    const d = typeof x === "number" && typeof y === "number" ? x - y : String(x ?? "").localeCompare(String(y ?? ""));
    return t.sortBy!.dir === "asc" ? d : -d;
  }) : rows;
  const shown = t.rows === "all" ? sorted : sorted.slice(0, t.rows);
  const max = Object.fromEntries(cols.map((c) => [c.field, Math.max(0, ...rows.map((r) => Number(r.values[c.field]) || 0))]));
  const fmtCell = (c: TableColumn, v: string | number | undefined): string =>
    v === undefined || v === "" ? "—" : c.format === "usd" || c.format === "usdBar" ? formatNumber(Number(v), { format: "full", decimals: 0 }, "usd")
      : c.format === "number" ? Number(v).toLocaleString("en-US") : c.format === "pct" ? `${Math.round(Number(v) * 100)}%` : String(v);
  const viewerVars = Object.fromEntries(Object.entries(opts.viewer ?? {}).map(([k, v]) => [`viewer.${k}`, v]));
  const resolve = (v: number | string) => (typeof v === "string" ? fillTemplate(v, viewerVars) : v);
  const styleOf = (r: TableRow) => t.highlights.find((h) => {
    const v = r.values[h.field], want = resolve(h.value);
    const nv = Number(v), nw = Number(want);
    return h.op === "=" ? String(v) === String(want) : Number.isFinite(nv) && Number.isFinite(nw) && (h.op === ">=" ? nv >= nw : nv <= nw);
  })?.style;
  return {
    columns: cols.map((c) => ({ field: c.field, label: c.label, format: c.format, max: c.format === "usdBar" ? max[c.field] : undefined })),
    rows: shown.map((r) => ({ key: r.key, href: r.href, style: styleOf(r),
      cells: cols.map((c) => { const v = r.values[c.field]; return { text: fmtCell(c, v), value: typeof v === "number" ? v : undefined, bar: c.format === "usdBar" && max[c.field] ? Number(v) / max[c.field] : undefined }; }) })),
    total: t.totalRow ? { label: `Total · ${rows.length}`, cells: cols.map((c, i) => i === 0 ? `Total · ${rows.length}`
      : ["usd", "usdBar", "number"].includes(c.format) ? fmtCell(c, c.field === "value" && opts.allRowsTotal !== undefined ? opts.allRowsTotal : rows.reduce((a, r) => a + (Number(r.values[c.field]) || 0), 0)) : null) } : undefined,
    more: sorted.length - shown.length,
  };
}

// ============================================================================ Whole settings

export interface ChartSettings {
  version: 1;
  type: ChartType;
  config: ChartConfig;
  data: DataBinding;
  labels: LabelSettings;
  colors: ColorSettings;
  interact: InteractionSettings;
  themeId: string;
  /** Only for the Table type (screenshots/87). */
  table?: TableSettings;
}

export function defaultSettings(type: ChartType, datasetId: DatasetId = "contributions"): ChartSettings {
  return {
    version: 1, type, config: {}, themeId: "civic-light",
    data: { datasetId, mapping: { value: { agg: "count" }, tooltip: [] }, filters: [], sort: "valueDesc", topN: 8, groupRest: true, refresh: "live" },
    labels: { dataLabels: { show: true, position: "end", format: "full", decimals: 0 }, renames: {}, axis: {}, legend: "bottom", referenceLines: [] },
    colors: { mode: "single", single: "chart-1", highlight: { color: "chart-2", others: "muted", label: true }, byCategory: {}, rules: [], elseColor: "muted" },
    interact: { click: "open", tooltip: true, emphasis: "self", dimOthers: true, enter: "grow", update: "morph", duration: 300, easing: "cubicOut" },
  };
}

/** Human-readable change list ("3 label changes on this chart", history rows). */
export function describeChanges(a: ChartSettings, b: ChartSettings): string[] {
  const out: string[] = [];
  if (a.type !== b.type) out.push(`Type: ${b.type}`);
  if (a.data.datasetId !== b.data.datasetId) out.push(`Dataset: ${DATASETS[b.data.datasetId].label}`);
  if (a.data.mapping.split !== b.data.mapping.split) out.push(b.data.mapping.split ? `Split by: ${b.data.mapping.split}` : "Split removed");
  if (JSON.stringify(a.data.filters) !== JSON.stringify(b.data.filters)) out.push("Filters changed");
  if (a.labels.title !== b.labels.title) out.push(`Title: “${b.labels.title ?? ""}”`);
  const renamed = Object.keys(b.labels.renames).filter((k) => a.labels.renames[k] !== b.labels.renames[k]).length;
  if (renamed) out.push(`Renamed ${renamed}`);
  if (a.labels.renamePattern !== b.labels.renamePattern) out.push(b.labels.renamePattern ? `Names: ${b.labels.renamePattern}` : "Name pattern removed");
  if (b.labels.referenceLines.filter((r) => r.on).length !== a.labels.referenceLines.filter((r) => r.on).length) out.push("Reference lines changed");
  if (JSON.stringify(a.colors) !== JSON.stringify(b.colors)) out.push(`Colors: ${b.colors.mode}`);
  if (JSON.stringify(a.interact) !== JSON.stringify(b.interact)) out.push("Interactions changed");
  if (a.themeId !== b.themeId) out.push(`Theme: ${b.themeId}`);
  return out;
}

// ============================================================================ Apply to an ECharts option

/** Types whose marks follow the Colors tab. The rest keep meaning colors (forecast, support/oppose, scales). */
export const RECOLORABLE = new Set<ChartType>(["bar", "column", "line", "area", "scatter", "donut", "radar", "treemap", "network", "networkLive", "tree", "radialTree", "sankey", "clustering"]);

/** Types that take reference lines (average / median / value) on their value axis. */
export const REFLINE_TYPES = new Set<ChartType>(["bar", "column", "line", "area", "scatter", "diverging"]);

export interface ApplyCtx {
  palette: Palette;
  unit: Unit;
  /** Extra values per row key for tokens, e.g. { nugent: { ward: 39 } }. */
  vars?: Record<string, Record<string, string | number>>;
  /** Row key by display label, so axis labels and tooltips can find their row. */
  keyOf?: (label: string) => string;
  reducedMotion?: boolean;
}

type Any = any; // eslint-disable-line @typescript-eslint/no-explicit-any
/** Deep copy of plain objects/arrays; functions (formatters) and other values are kept by reference. */
const cloneOption = <T,>(v: T): T => (Array.isArray(v) ? v.map(cloneOption) as T
  : v && typeof v === "object" && Object.getPrototypeOf(v) === Object.prototype
    ? Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([k, x]) => [k, cloneOption(x)])) as T : v);
const arr = <T,>(x: T | T[] | undefined): T[] => (x === undefined ? [] : Array.isArray(x) ? x : [x]);

/**
 * Layer the user's Labels / Colors / Interact settings onto an option from `buildOption`. Never touches data
 * values or the meaning colors of forecast/semantic charts; returns a new option object.
 */
export function applySettings(option: EChartsCoreOption, s: ChartSettings, ctx: ApplyCtx): EChartsCoreOption {
  const o: Any = cloneOption(option);
  const keyOf = ctx.keyOf ?? ((l: string) => l);
  const varsFor = (key: string, label: string) => ({ ...(ctx.vars?.[key] ?? {}), name: label });
  const shown = (label: string) => displayName(keyOf(label), label, varsFor(keyOf(label), label), s.labels);
  const horizontal = arr(o.yAxis)[0]?.type === "category";

  // Category axis labels follow renames
  for (const ax of [...arr(o.xAxis), ...arr(o.yAxis)]) if (ax?.type === "category" && Array.isArray(ax.data))
    ax.axisLabel = { ...ax.axisLabel, formatter: (v: string) => shown(String(v)) };

  // Axis titles
  const valueAxis = horizontal ? arr(o.xAxis)[0] : arr(o.yAxis)[0];
  const catAxis = horizontal ? arr(o.yAxis)[0] : arr(o.xAxis)[0];
  if (valueAxis && s.labels.axis.value) Object.assign(valueAxis, { name: s.labels.axis.value, nameLocation: "middle", nameGap: 26, show: true });
  if (catAxis && s.labels.axis.category) Object.assign(catAxis, { name: s.labels.axis.category });

  // Legend
  if (s.labels.legend === "off") o.legend = { ...(o.legend ?? {}), show: false };
  else if (o.legend) o.legend = { ...o.legend, show: true, top: s.labels.legend === "top" ? 0 : undefined, bottom: s.labels.legend === "bottom" ? 0 : undefined,
    right: s.labels.legend === "right" ? 0 : undefined, orient: s.labels.legend === "right" ? "vertical" : "horizontal" };

  // Tooltip template
  if (s.interact.tooltip === false) o.tooltip = { ...(o.tooltip ?? {}), show: false };
  else if (s.labels.tooltip || ((s.type === "bar" || s.type === "column") && catAxis?.type === "category")) {
    // Bars name their items by row key; the tooltip shows the row's label (its category on the axis), then the template.
    const tpl = s.labels.tooltip ?? (ctx.unit === "usd" ? "{name} · {value:$,0}" : "{name} · {value:,0}");
    const cats = arr<Any>(catAxis?.data);
    o.tooltip = { ...(o.tooltip ?? {}), formatter: (pp: Any) => { const p = Array.isArray(pp) ? pp[0] : pp;
      const label = String((catAxis?.type === "category" && typeof p.dataIndex === "number" && cats[p.dataIndex] !== undefined ? cats[p.dataIndex] : p.name) ?? "");
      const value = Array.isArray(p.value) ? Number(p.value.at(-1)) : Number(p.value);
      const key = keyOf(label); return fillTemplate(tpl, { ...varsFor(key, label), name: shown(label), value }); } };
  }

  const recolor = RECOLORABLE.has(s.type);
  for (const se of arr<Any>(o.series)) {
    // Data labels
    if (["bar", "line", "pie", "scatter"].includes(se.type) && !se.stack) {
      const pos = se.type === "pie" ? (s.labels.dataLabels.position === "inside" ? "inside" : "outside")
        : horizontal ? { inside: "insideRight", end: "right", outside: "right" }[s.labels.dataLabels.position]
        : { inside: "insideTop", end: "top", outside: "top" }[s.labels.dataLabels.position];
      se.label = { ...(se.label ?? {}), show: s.labels.dataLabels.show, position: pos,
        color: s.labels.dataLabels.position === "inside" ? "#FFFFFF" : se.label?.color,
        formatter: (p: Any) => formatNumber(Array.isArray(p.value) ? Number(p.value.at(-1)) : Number(p.value), s.labels.dataLabels, ctx.unit) };
    }
    // Colors (per data item)
    if (recolor && Array.isArray(se.data) && ["bar", "line", "pie", "scatter"].includes(se.type)) {
      se.data = se.data.map((d: Any, i: number) => {
        const item = d !== null && typeof d === "object" && !Array.isArray(d) ? { ...d } : { value: d };
        const label = String(item.name ?? arr<Any>(catAxis?.data)[i] ?? "");
        const value = Array.isArray(item.value) ? Number(item.value.at(-1)) : Number(item.value);
        const color = colorFor(keyOf(label), value, s.colors, ctx.palette);
        item.itemStyle = { ...(item.itemStyle ?? {}), color };
        if (s.colors.mode === "highlight" && s.colors.highlight.label && keyOf(label) === s.colors.highlight.key) item.label = { show: true, fontWeight: 700 };
        return item;
      });
      if (se.type === "line") se.lineStyle = { ...(se.lineStyle ?? {}), color: resolveColor(s.colors.mode === "single" ? s.colors.single : s.colors.highlight.color, ctx.palette) };
    }
    // Reference lines
    const lines = s.labels.referenceLines.filter((r) => r.on);
    // Only on cartesian charts (seats, the month calendar and clusters use scatter on other coordinate systems).
    if (lines.length && REFLINE_TYPES.has(s.type) && o.xAxis && o.yAxis && (!se.coordinateSystem || se.coordinateSystem === "cartesian2d")
      && ["bar", "line", "scatter"].includes(se.type) && !se.stack && se.id !== "base" && se.id !== "rest") {
      se.markLine = { symbol: "none", silent: true, lineStyle: { color: ctx.palette.text, type: "dashed", width: 1.5 },
        label: { color: ctx.palette.text, fontWeight: 500, fontSize: 11, position: horizontal ? "start" : "insideEndTop", distance: horizontal ? [8, -2] : 4,
          formatter: (p: Any) => { const v = formatNumber(Number(p.value), { ...s.labels.dataLabels, format: "full", decimals: 0 }, ctx.unit);
            return p.name === "Average" ? `Avg ${v}` : p.name === "Median" ? `Median ${v}` : p.name ? `${p.name} ${v}` : v; } },
        data: lines.map((r) => r.kind === "value" ? { name: r.label ?? "", [horizontal ? "xAxis" : "yAxis"]: r.value } : { type: r.kind, name: r.label ?? (r.kind === "average" ? "Average" : "Median") }) };
    }
    // Bar corners (72 Bar options)
    if (se.type === "bar" && !se.stack && s.config.corners) {
      const r = { square: 0, soft: 3, round: 8 }[s.config.corners];
      const radius = horizontal ? [0, r, r, 0] : [r, r, 0, 0];
      se.itemStyle = { ...(se.itemStyle ?? {}), borderRadius: radius };
      if (Array.isArray(se.data)) se.data = se.data.map((d: Any) => (d !== null && typeof d === "object" && !Array.isArray(d) ? { ...d, itemStyle: { ...(d.itemStyle ?? {}), borderRadius: radius } } : d));
    }
    if (lines.length && horizontal && o.grid && !Array.isArray(o.grid)) o.grid = { ...o.grid, bottom: Math.max(Number(o.grid.bottom) || 0, 22) };
    // Hover emphasis
    se.emphasis = { ...(se.emphasis ?? {}), focus: s.interact.emphasis === "none" ? "none" : s.interact.emphasis === "series" ? "series" : "self" };
    se.blur = s.interact.dimOthers ? { itemStyle: { opacity: 0.35 }, label: { opacity: 0.35 } } : undefined;
    se.selectedMode = s.interact.click === "filter" ? "single" : false;
    if (s.interact.click === "filter") se.select = { itemStyle: { borderColor: ctx.palette.text, borderWidth: 2 } };
  }

  // Motion
  const anim = !ctx.reducedMotion && s.interact.enter !== "none";
  o.animation = anim;
  o.animationDuration = s.interact.enter === "grow" ? s.interact.duration : 0;
  o.animationDurationUpdate = s.interact.update === "none" || ctx.reducedMotion ? 0 : s.interact.duration;
  o.animationEasing = o.animationEasingUpdate = s.interact.easing;
  return o;
}

/** Widget header text (titles live in the DOM header, not inside the canvas). */
export const headerText = (s: ChartSettings, fallback: { title: string; subtitle?: string }, vars: Record<string, string | number> = {}) => ({
  title: s.labels.title ? fillTemplate(s.labels.title, vars) : fallback.title,
  subtitle: s.labels.subtitle ? fillTemplate(s.labels.subtitle, vars) : fallback.subtitle,
});
